import React, { useState, useEffect } from 'react';
import { Button, Input, Select, Card, Statistic, Spin, Empty } from 'antd';
import { PlusOutlined, ReloadOutlined, SearchOutlined, InboxOutlined, CheckCircleOutlined, ClockCircleOutlined, AppstoreOutlined } from '@ant-design/icons';
import AddIndustryForm from '../components/admin/industry/AddIndustryForm';
import IndustryTable from '../components/admin/industry/IndustryTable';
import Modal from '../components/common/Modal';
import AdminLayout from '../components/layout/AdminLayout';
import { fetchIndustries, createIndustries, updateIndustries, deleteIndustries } from '../api/industryApi';

const { Option } = Select;

const IndustryManagement = () => {
  const [industries, setIndustries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingIndustry, setEditingIndustry] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all');

  // Add this useEffect to debug
  useEffect(() => {
    //console.log('Modal state changed:', isModalOpen);
  }, [isModalOpen]);

  const loadIndustries = async () => {
    setLoading(true);
    try {
      const response = await fetchIndustries();
      //console.log('API Response:', response);
      
      if (response && response.success) {
        // Handle the API response structure correctly
        const industriesData = response.data || [];
        //console.log('Industries Data:', industriesData);
        setIndustries(Array.isArray(industriesData) ? industriesData : []);
      } else {
        console.error('Failed to fetch industries - invalid response');
        setIndustries([]);
      }
    } catch (error) {
      console.error('Error fetching industries:', error);
      setIndustries([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadIndustries();
  }, []);

  const openAddModal = () => {
    //console.log('openAddModal called');
    setEditingIndustry(null);
    setIsModalOpen(true);
  };

  const openEditModal = (industry) => {
    //console.log('openEditModal called', industry);
    setEditingIndustry(industry);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    //console.log('closeModal called');
    setIsModalOpen(false);
    setEditingIndustry(null);
  };

  const handleSaveIndustry = async (industryData) => {
    try {
      console.log('Saving industry data:', industryData);
      
      // Remove the _id from the data being sent (it's in the URL)
      const { _id, createdAt, updatedAt, __v, ...cleanData } = industryData;
      
      // For the new industry form structure, send as JSON
      const response = editingIndustry 
        ? await updateIndustries(editingIndustry._id, cleanData)
        : await createIndustries(cleanData);

      //console.log('Save response:', response);

      if (response && response.success) {
        await loadIndustries();
        closeModal();
      } else {
        console.error('Failed to save industry:', response);
      }
    } catch (error) {
      console.error('Error saving industry:', error);
    }
  };

  const handleDeleteIndustry = async (id) => {
    if (window.confirm('Are you sure you want to delete this industry? This action cannot be undone.')) {
      try {
        const response = await deleteIndustries(id);
        if (response) {
          await loadIndustries();
        } else {
          console.error('Failed to delete industry');
        }
      } catch (error) {
        console.error('Error deleting industry:', error);
      }
    }
  };

  const filteredIndustries = Array.isArray(industries) 
    ? industries.filter(industry => {
        const matchesSearch = industry.sectorName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                             industry.briefDescription?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesFilter = filter === 'all' || industry.status === filter;
        return matchesSearch && matchesFilter;
      })
    : [];

  return (
    <>
      <div className="max-w-9xl mx-auto w-full" style={{ fontFamily: 'Rubik, sans-serif' }}>
        <div className="p-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
            <div>
              {/* <h1 className="text-2xl font-bold text-gray-800">Industry Management</h1>
              <p className="text-gray-600">Manage all industries in the system</p> */}
            </div>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={openAddModal}
              size="large"
              style={{
                fontFamily: 'Rubik, sans-serif',
                fontSize: '16px',
                fontWeight: 400,
                backgroundColor: '#470172',
                borderColor: '#470172',
                height: 'auto',
                padding: '10px 20px'
              }}
            >
              Add Industry
            </Button>
          </div>

          {/* Search and Filter */}
          <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <Input
                size="large"
                placeholder="Search industries..."
                prefix={<SearchOutlined style={{ color: '#470172' }} />}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  fontFamily: 'Rubik, sans-serif',
                  fontSize: '16px',
                  fontWeight: 400,
                  color: '#470172'
                }}
              />
            </div>
            <div>
              <Select
                size="large"
                value={filter}
                onChange={(value) => setFilter(value)}
                style={{
                  width: '100%',
                  fontFamily: 'Rubik, sans-serif',
                  fontSize: '16px',
                  fontWeight: 400,
                  color: '#470172'
                }}
              >
                <Option value="all">All Status</Option>
                <Option value="active">Active</Option>
                <Option value="inactive">Inactive</Option>
              </Select>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-6">
            <Card>
              <Statistic
                title={<span style={{ fontFamily: 'Rubik, sans-serif', fontSize: '16px', fontWeight: 400, color: '#470172' }}>Total Industries</span>}
                value={Array.isArray(industries) ? industries.length : 0}
                prefix={<InboxOutlined style={{ color: '#470172' }} />}
                valueStyle={{ fontFamily: 'Rubik', fontSize: '28px', fontWeight: 400, color: '#470172' }}
              />
            </Card>
            <Card>
              <Statistic
                title={<span style={{ fontFamily: 'Rubik, sans-serif', fontSize: '16px', fontWeight: 400, color: '#470172' }}>Active</span>}
                value={Array.isArray(industries) ? industries.filter(i => i.status === 'active').length : 0}
                prefix={<CheckCircleOutlined style={{ color: '#470172' }} />}
                valueStyle={{ fontFamily: 'Rubik', fontSize: '28px', fontWeight: 400, color: '#470172' }}
              />
            </Card>
            <Card>
              <Statistic
                title={<span style={{ fontFamily: 'Rubik, sans-serif', fontSize: '16px', fontWeight: 400, color: '#470172' }}>Inactive</span>}
                value={Array.isArray(industries) ? industries.filter(i => i.status === 'inactive').length : 0}
                prefix={<ClockCircleOutlined style={{ color: '#470172' }} />}
                valueStyle={{ fontFamily: 'Rubik', fontSize: '28px', fontWeight: 400, color: '#470172' }}
              />
            </Card>
            <Card>
              <Statistic
                title={<span style={{ fontFamily: 'Rubik, sans-serif', fontSize: '16px', fontWeight: 400, color: '#470172' }}>Categories</span>}
                value={Array.isArray(industries) ? [...new Set(industries.map(i => i.category))].length : 0}
                prefix={<AppstoreOutlined style={{ color: '#470172' }} />}
                valueStyle={{ fontFamily: 'Rubik', fontSize: '28px', fontWeight: 400, color: '#470172' }}
              />
            </Card>
          </div>

          {/* Industry Table */}
          <Card
            title={
              <div>
                {/* <h3 style={{ fontFamily: 'Rubik, sans-serif', fontSize: '20px', fontWeight: 400, color: '#470172', margin: 0 }}>Industries</h3> */}
                <p style={{ fontFamily: 'Rubik, sans-serif', fontSize: '15px', fontWeight: 400, color: '#470172', margin: '4px 0 0 0', opacity: 0.7 }}>
                  List of all industries 
                </p>
              </div>
            }
            extra={
              <Button
                icon={<ReloadOutlined spin={loading} />}
                onClick={loadIndustries}
                disabled={loading}
                style={{
                  fontFamily: 'Rubik, sans-serif',
                  fontSize: '14px',
                  fontWeight: 400,
                  color: '#470172',
                  borderColor: '#470172'
                }}
              >
                Refresh
              </Button>
            }
          >
            {loading ? (
              <div className="flex justify-center items-center p-8">
                <Spin size="large" />
              </div>
            ) : filteredIndustries.length > 0 ? (
              <IndustryTable
                industries={filteredIndustries}
                onEdit={openEditModal}
                onDelete={handleDeleteIndustry}
              />
            ) : (
              <Empty
                description={
                  <span style={{ fontFamily: 'Rubik, sans-serif', fontSize: '16px', fontWeight: 400, color: '#470172' }}>
                    {searchTerm || filter !== 'all' ? 'No industries found' : 'Get started by creating a new industry.'}
                  </span>
                }
              >
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={openAddModal}
                  size="large"
                  style={{
                    fontFamily: 'Rubik, sans-serif',
                    fontSize: '16px',
                    fontWeight: 400,
                    backgroundColor: '#470172',
                    borderColor: '#470172'
                  }}
                >
                  New Industry
                </Button>
              </Empty>
            )}
          </Card>
        </div>
      </div>

      {isModalOpen && (
        <Modal 
          isOpen={isModalOpen} 
          onClose={closeModal} 
          title={editingIndustry ? 'Edit Industry' : 'Add New Industry'}
          width="90%"
          style={{ top: 20 }}
          bodyStyle={{ height: 'calc(90vh - 100px)', overflow: 'auto' }}
        >
          <AddIndustryForm
            editingIndustry={editingIndustry}
            onSave={handleSaveIndustry}
            onCancel={closeModal}
          />
        </Modal>
      )}
    </>
  );
};

export default IndustryManagement;
